import React, {useEffect, useState} from 'react';
import {Button, Checkbox, Divider, Input, Modal, Statistic, Tag, Typography} from "antd";
import axios from "axios";

const {Paragraph, Text} = Typography;

const DEFAULT_RENEWAL_FEE = 2500;
const DEFAULT_CC_FEE_PCT = 0.04;

const DEFAULT_RULES_DOCUMENT = '/2027_Stallion_Enrollment_Rules_.pdf';
const RULES_VERSION = '2027 Stallion Enrollment Rules';

const formatUSD = (n) => `$${Number(n).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

const RenewalSignature = ({values, setFieldValue, remainingBalance}) => {
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [rulesOpened, setRulesOpened] = useState(false);
  const [agreed, setAgreed] = useState(Boolean(values?.renewalSigned));
  const [signerName, setSignerName] = useState(values?.renewalSignedName || '');
  const [renewalFee, setRenewalFee] = useState(DEFAULT_RENEWAL_FEE);
  const [ccFeePct, setCcFeePct] = useState(DEFAULT_CC_FEE_PCT);
  const [rulesDocumentUrl, setRulesDocumentUrl] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${process.env.REACT_APP_MANAGEMENT_API_URL}/settings`)
      .then((res) => {
        if (!active) return;
        if (res?.data?.sireRenewalFee != null) {
          setRenewalFee(Number(res.data.sireRenewalFee));
        }
        if (res?.data?.ccProcessingFeePct != null) {
          setCcFeePct(Number(res.data.ccProcessingFeePct));
        }
        if (res?.data?.rulesDocumentUrl) {
          setRulesDocumentUrl(res.data.rulesDocumentUrl);
        }
      })
      .catch(() => {
        // Fall back to the built-in defaults if settings can't be fetched.
      });
    return () => {
      active = false;
    };
  }, []);

  // Prefer the owner's outstanding balance when present; otherwise use the base renewal fee.
  const due = typeof remainingBalance === 'number' ? remainingBalance : renewalFee;
  const amountDueValue = due * (1 + ccFeePct);
  const amountDue = formatUSD(amountDueValue);
  const renewalFeeText = formatUSD(renewalFee);
  const stallionName = values?.name || 'this stallion';

  const legalText = `I have read and accept the Tomorrow’s Legends Enrollment Rules. I understand and agree that by signing this I am renewing “${stallionName}” in Tomorrow’s Legends for one year. I understand and agree that payment in the amount of ${amountDue} is required with renewal. By signing this I agree to pay the renewal fee of ${renewalFeeText}. I understand failure to pay will result in late fees applied in the amount of $100 per month. Overdue accounts will be subject to collection action resulting in further collection costs and reasonable attorney’s fees.`;

  const openRulesFlow = () => {
    setRulesModalOpen(true);
  };

  const agreeToRules = () => {
    setRulesModalOpen(false);
    setSignModalOpen(true);
  };

  const canSign = agreed && signerName.trim().length > 0;

  const confirmSignature = () => {
    if (!canSign) return;
    setFieldValue('renewalSigned', true);
    setFieldValue('renewalSignedAt', new Date().toISOString());
    setFieldValue('renewalSignedName', signerName.trim());
    setFieldValue('renewalSignedStatement', legalText);
    setFieldValue('renewalSignedRulesVersion', RULES_VERSION);
    setSignModalOpen(false);
  };

  const clearSignature = () => {
    setAgreed(false);
    setRulesOpened(false);
    setSignerName('');
    setFieldValue('renewalSigned', false);
    setFieldValue('renewalSignedAt', null);
    setFieldValue('renewalSignedName', '');
    setFieldValue('renewalSignedStatement', '');
    setFieldValue('renewalSignedRulesVersion', '');
  };

  return (
    <>
      <Divider plain/>
      <Statistic
        title="Amount Due Today (includes card processing fee):"
        value={amountDue}
      />

      <div style={{marginTop: '24px'}}>
        {values?.renewalSigned ? (
          <div>
            <Text type="success" strong>
              ✓ Rules read and signed
              {values?.renewalSignedName ? ` by ${values.renewalSignedName}` : ''}
              {values?.renewalSignedAt ? ` on ${new Date(values.renewalSignedAt).toLocaleString()}` : ''}.
            </Text>
            <div style={{marginTop: '8px'}}>
              <Button size="small" onClick={() => { clearSignature(); openRulesFlow(); }}>
                Review / re-sign
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button type="primary" onClick={openRulesFlow}>
              Read Rules &amp; Sign
            </Button>
            <Tag style={{marginLeft: '8px'}} color="red">
              *Required before payment
            </Tag>
          </div>
        )}
      </div>

      <Modal
        title="Tomorrow’s Legends Enrollment Rules"
        open={rulesModalOpen}
        onCancel={() => setRulesModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setRulesModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="agree" type="primary" disabled={!rulesOpened} onClick={agreeToRules}>
            I have opened and agree to the Rules
          </Button>,
        ]}
      >
        <Paragraph>
          Please open and read the Tomorrow’s Legends Enrollment Rules, then agree to continue to signing.
        </Paragraph>
        <Paragraph>
          <a
            href={rulesDocumentUrl || DEFAULT_RULES_DOCUMENT}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setRulesOpened(true)}
          >
            Open the Tomorrow’s Legends Enrollment Rules (PDF)
          </a>
        </Paragraph>
        {!rulesOpened && (
          <Text type="secondary">
            You must open the rules document to enable the “I agree” button.
          </Text>
        )}
      </Modal>

      <Modal
        title="Renewal Signature"
        open={signModalOpen}
        onCancel={() => setSignModalOpen(false)}
        footer={[
          <Button key="back" onClick={() => { setSignModalOpen(false); setRulesModalOpen(true); }}>
            Back
          </Button>,
          <Button key="sign" type="primary" disabled={!canSign} onClick={confirmSignature}>
            Sign &amp; Continue
          </Button>,
        ]}
      >
        <Checkbox
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          style={{alignItems: 'flex-start'}}
        >
          <span style={{display: 'inline-block'}}>{legalText}</span>
        </Checkbox>
        <div style={{marginTop: '16px'}}>
          <Text strong>Type your full legal name to sign:</Text>
          <Input
            style={{marginTop: '6px'}}
            placeholder="Full legal name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
          />
        </div>
        <div style={{marginTop: '12px'}}>
          <Text type="secondary">
            {canSign
              ? `Signing as “${signerName.trim()}”. Your signature will be time-stamped ${new Date().toLocaleString()} when you continue.`
              : 'Check the box and type your full name to sign. Your signature will be time-stamped.'}
          </Text>
        </div>
      </Modal>
    </>
  );
};

export default RenewalSignature;
